import React from 'react';
import { Box, Printer, Monitor, Calendar, Search, Clock } from 'lucide-react';

const AssetTracking: React.FC = () => {
  // Mock data - in real app this would come from floor sensors and asset tracking systems
  const assetData = {
    overview: {
      totalAssets: 425,
      activelyTracked: 385,
      inUse: 312,
      maintenance: 8
    },
    equipment: [
      {
        id: 'PR-001',
        type: 'Printer',
        location: 'Floor 2 - North Wing',
        status: 'Active',
        lastUsed: '10 mins ago',
        utilization: 85,
        maintenanceStatus: 'Due in 5 days'
      },
      {
        id: 'LT-125',
        type: 'Laptop Cart',
        location: 'Floor 1 - Training Room',
        status: 'In Use',
        lastUsed: 'Currently in use',
        utilization: 92,
        maintenanceStatus: 'OK'
      },
      {
        id: 'AV-012',
        type: 'AV Equipment',
        location: 'Floor 3 - Conference Room A',
        status: 'Maintenance',
        lastUsed: '2 days ago',
        utilization: 45,
        maintenanceStatus: 'Under repair'
      }
    ],
    utilization: {
      meetingRooms: [
        { room: 'Conference A', equipment: 12, usage: 85, status: 'All Systems OK' },
        { room: 'Conference B', equipment: 8, usage: 75, status: 'Projector Issue' },
        { room: 'Training Room', equipment: 15, usage: 92, status: 'All Systems OK' },
        { room: 'Board Room', equipment: 10, usage: 65, status: 'All Systems OK' }
      ],
      recentActivity: [
        { time: '10:45 AM', event: 'Printer PR-001 paper low', type: 'warning' },
        { time: '10:30 AM', event: 'Laptop Cart LT-125 checked out', type: 'info' },
        { time: '10:15 AM', event: 'AV-012 maintenance started', type: 'alert' },
        { time: '10:00 AM', event: 'Conference B projector issue reported', type: 'warning' }
      ]
    },
    maintenance: {
      scheduled: [
        {
          asset: 'Printer PR-002',
          date: '2024-03-25',
          type: 'Routine',
          technician: 'John Smith'
        },
        {
          asset: 'AV System AV-012',
          date: '2024-03-22',
          type: 'Repair',
          technician: 'Sarah Johnson'
        }
      ],
      history: [
        {
          asset: 'Laptop Cart LT-125',
          date: '2024-03-15',
          type: 'Inspection',
          notes: 'All systems functioning normally'
        },
        {
          asset: 'Printer PR-001',
          date: '2024-03-10',
          type: 'Repair',
          notes: 'Replaced toner cartridge'
        }
      ]
    },
    lostItems: [
      {
        id: 'LI-001',
        description: 'Conference Room Remote',
        lastSeen: 'Floor 2 - Room 2B',
        status: 'Searching',
        reportedBy: 'Jane Doe'
      },
      {
        id: 'LI-002',
        description: 'Wireless Presenter',
        lastSeen: 'Floor 1 - Training Room',
        status: 'Found',
        reportedBy: 'Mike Wilson'
      }
    ]
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-500';
      case 'in use':
        return 'text-blue-500';
      case 'maintenance':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'warning':
        return 'text-yellow-500';
      case 'alert':
        return 'text-red-500';
      case 'info':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Asset Tracking & Management</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Box className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Total Assets</h3>
          </div>
          <p className="text-3xl font-bold">{assetData.overview.totalAssets}</p>
          <p className="text-gray-500">Tracked items</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Monitor className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">In Use</h3>
          </div>
          <p className="text-3xl font-bold">{assetData.overview.inUse}</p>
          <p className="text-gray-500">Active equipment</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Search className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Lost Items</h3>
          </div>
          <p className="text-3xl font-bold">{assetData.lostItems.length}</p>
          <p className="text-gray-500">Being tracked</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Calendar className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Maintenance</h3>
          </div>
          <p className="text-3xl font-bold">{assetData.overview.maintenance}</p>
          <p className="text-gray-500">Items scheduled</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Equipment Status</h3>
          <div className="space-y-4">
            {assetData.equipment.map((item) => (
              <div key={item.id} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{item.type} ({item.id})</h4>
                    <p className="text-sm text-gray-500">{item.location}</p>
                  </div>
                  <span className={`font-medium ${getStatusColor(item.status)}`}>
                    {item.status}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-gray-500">
                    Last used: {item.lastUsed}
                  </span>
                  <span className="text-sm text-gray-500">
                    {item.maintenanceStatus}
                  </span>
                </div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`rounded-full h-2 ${
                        item.utilization >= 85 ? 'bg-red-500' :
                        item.utilization >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${item.utilization}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {assetData.utilization.recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start justify-between border-b pb-4 last:border-b-0">
                <div>
                  <p className={`font-medium ${getEventTypeColor(activity.type)}`}>
                    {activity.event}
                  </p>
                  <p className="text-sm text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Maintenance Schedule</h3>
          <div className="space-y-4">
            {assetData.maintenance.scheduled.map((maintenance, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{maintenance.asset}</h4>
                    <p className="text-sm text-gray-600">{maintenance.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{maintenance.date}</p>
                    <p className="text-sm text-gray-500">{maintenance.technician}</p>
                  </div>
                </div>
              </div>
            ))}
            <div className="mt-4 pt-4 border-t">
              <h4 className="font-medium mb-2">Recent Maintenance</h4>
              {assetData.maintenance.history.map((history, index) => (
                <div key={index} className="text-sm text-gray-600 mb-2">
                  <p className="font-medium">{history.asset} - {history.date}</p>
                  <p>{history.notes}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Lost Item Tracking</h3>
          <div className="space-y-4">
            {assetData.lostItems.map((item) => (
              <div key={item.id} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{item.description}</h4>
                    <p className="text-sm text-gray-500">
                      Last seen: {item.lastSeen}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    item.status === 'Found' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">Reported by: {item.reportedBy}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetTracking; 